// Refetch guards for a viewed entity whose content changed outside this
// store: the in-tab cache-invalidation bus and the cross-tab sync channel.
import { useOrganizationManager } from "@/composables/organization-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { onEntityChange } from "@/stores/cache-invalidation";
import { useSyncStore } from "@/stores/sync-store";

export interface ExternalRefreshOpts {
    entityType: string;
    currentEntityId: () => string | undefined;
    isDirty: () => boolean;
    refetch: () => void;
}

/**
 * Whether refetching the entity now would clobber local state: the user has
 * unsaved edits (`isDirty`), or edits already queued/in flight in the sync
 * store. The latter matters because stores persist their artefacts
 * optimistically when queueing a save — `isDirty` turns false before the
 * backend write lands, and a refetch in that window pulls stale server
 * content over the user's latest changes (seen as e.g. the BPMN diagram
 * flashing back to a pre-edit version and forward again). Once the pending
 * edit's own sync completes it re-notifies, so a skipped refetch is always
 * followed by one that runs.
 *
 * Exported for the stores' LANDING-TIME guard: checking only here, when the
 * notification arrives, leaves a race open — edits made while the refetch's
 * GET is in flight would still be overwritten when its response commits. So
 * every refetch re-evaluates this right before writing fetched state into
 * the store, and drops the response if it now returns true. A dropped
 * commit self-heals the same way a skipped refetch does: the edit that
 * caused it syncs, re-notifies, and the next refetch commits.
 */
export const wouldClobberLocalChanges = (opts: ExternalRefreshOpts): boolean =>
    opts.isDirty() ||
    useSyncStore().hasPendingChanges(opts.entityType, opts.currentEntityId());

/**
 * Refetch a viewed entity's content when an EXTERNAL change to it lands on the
 * cache-invalidation bus. A thread catch-up or conflict resolution mutates the
 * content at the SAME revision (`thread/<id>`), which the revision-keyed fetch
 * cannot see — so without this the editor stays stale until a manual page
 * refresh (the exact gap reported for "keep main → product description doesn't
 * update on the branch").
 *
 * Skips while a refetch would clobber local changes (see
 * `wouldClobberLocalChanges`), so neither a self-edit's own post-success
 * notification nor a soft-blocked edit awaiting a retry is clobbered. The
 * refetch is a GET and must not emit `notifyEntityChanged` itself, so there is
 * no feedback loop. Registers once for the (singleton) store's lifetime,
 * mirroring the steks-store subscription.
 *
 * Returns the bus disposer (rarely needed for app-lifetime stores).
 */
export function refreshOnExternalEntityChange(
    opts: ExternalRefreshOpts
): () => void {
    return onEntityChange(
        (change) =>
            change.origin !== "local-sync" &&
            change.entityType === opts.entityType &&
            change.entityId === opts.currentEntityId() &&
            !wouldClobberLocalChanges(opts),
        () => opts.refetch()
    );
}

/**
 * Refetch a viewed entity's content when ANOTHER TAB syncs it — the
 * cache-invalidation bus only carries this tab's syncs, so cross-tab changes
 * arrive via the sync BroadcastChannel instead. Applies the same
 * clobber guard as `refreshOnExternalEntityChange`.
 *
 * `enabled` preserves store-specific gating (e.g. "only when a stek is
 * loaded"); it defaults to always-on. Registers once for the (singleton)
 * store's lifetime — the channel outlives any component scope.
 */
export function refreshOnCrossTabEntitySync(
    opts: ExternalRefreshOpts & { enabled?: () => boolean }
): void {
    const { organization } = useOrganizationManager();
    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === opts.entityType &&
                identity.entityId === opts.currentEntityId() &&
                (opts.enabled ? opts.enabled() : true) &&
                !wouldClobberLocalChanges(opts)
            ) {
                opts.refetch();
            }
        }
    );
}
